import { FilteringList, ListItem } from '@tableau/widgets';
import React from 'react';
import * as ReactDOM from 'react-dom';
import _ from 'underscore';

import { SearchSuggestionsService } from '~content/services/SearchSuggestionsService';

import { Messages } from '../../codegen/Localize';
import { imgPath } from '../../common/utilities/ImgUrl';
import { ListResult } from '../../ts/fetchers/ListResult';
import { IGroup } from '../../ts/ServerApi/types/IGroup';
import { IUser, IUserName } from '../../ts/ServerApi/types/IUser';
import { PermissionsGridStyles } from '../styles/PermissionsGridStyle';

export interface GranteeListItem extends ListItem {
  displayText?: string; // can be used to sort list items
  groupInfo?: IGroup;       // set if the list item is a group
  user?: IUser;         // set if the list item is a user
}

export interface GranteeSearchFieldProps {
  onGranteeSelect: (group?: IGroup, user?: IUser) => void;
}

export interface GranteeSearchFieldState {
  searchResults: GranteeListItem[];
  searchText: string;
}

const GranteeSearchFieldDefaultState: GranteeSearchFieldState = Object.freeze({ searchResults: [], searchText: '' });

export class GranteeSearchField extends React.PureComponent<GranteeSearchFieldProps, GranteeSearchFieldState>{
  public static readonly DEBOUNCE_MILLISECONDS = 250;
  public static readonly INPUT_ID = 'grantee-search-field-input';
  public static readonly LIST_ID = 'grantee-search-result-list';
  public static readonly GROUP_ID_PREFIX = 'Group_';
  public static readonly USER_ID_PREFIX = 'User_';

  private isMouseOverDropdown: boolean = false; // tracking if interacting with the dropdown
  private dropdownClassName: string = 'showPopup';
  private groupIcon: string = imgPath('users_groups_large.svg');
  private userIcon: string = imgPath('user_single_large.svg');

  public state: GranteeSearchFieldState = { ...GranteeSearchFieldDefaultState };

  public componentDidMount() {
    this.undebouncedSearch(this.state.searchText);
  }

  public groupToGranteeListItem = (group: IGroup): GranteeListItem => {
    const content = (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchResultContainer}>
        <span className={PermissionsGridStyles.EditableGranteeCellSearchResultImgContainer}>
          <img aria-label={Messages.group()}
            className={PermissionsGridStyles.EditableGranteeCellSearchResultImg}
            src={this.groupIcon} />
        </span>
        {group.name}
      </div>
    );
    return {
      content,
      displayText: group.name,
      groupInfo: group,
      tooltip: Messages.permissionsGranteeFullyQualifiedName_group({
        DOMAIN_NAME: group.domainName,
        GROUP_NAME: group.name,
      }),
      user: undefined,
      value: `${GranteeSearchField.GROUP_ID_PREFIX + group.id}`,
    };
  }

  public userToGranteeListItem = (user: IUserName): GranteeListItem => {
    const content = (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchResultContainer}>
        <span className={PermissionsGridStyles.EditableGranteeCellSearchResultImgContainer}>
          <img aria-label={Messages.user()}
            className={PermissionsGridStyles.EditableGranteeCellSearchResultImg}
            src={this.userIcon} />
        </span>
        {user.displayName}
      </div>
    );
    return {
      content,
      displayText: user.displayName,
      groupInfo: undefined,
      tooltip: Messages.permissionsGranteeFullyQualifiedName_user({
        DISPLAY_NAME: user.displayName,
        DOMAIN_NAME: user.domainName,
        USER_NAME: user.username,
      }),
      user,
      value: `${GranteeSearchField.USER_ID_PREFIX + user.id}`,
    };
  }

  // public for testing purposes
  public fetchSearchResults = (searchText: string): Promise<[ListResult<IGroup>, ListResult<IUserName>]> => {
    // fetch the first 10 matching users/groups
    const groupQuery: Promise<ListResult<IGroup>> = SearchSuggestionsService.groups(searchText).getSlice(0, 10);
    const userQuery: Promise<ListResult<IUserName>> = SearchSuggestionsService.users(searchText).getSlice(0, 10);
    return Promise.all([groupQuery, userQuery]);
  }

  private undebouncedSearch = async (searchText: string) => {
    const results = await this.fetchSearchResults(searchText);
    const groupListItems: GranteeListItem[] = results[0].items.map(this.groupToGranteeListItem);
    const userListItems: GranteeListItem[] = results[1].items.map(this.userToGranteeListItem);
    const sortedListItems: GranteeListItem[] = [...groupListItems, ...userListItems].sort((a, b) => (a.displayText > b.displayText) ? 1 : -1);
    if (searchText === this.state.searchText) {
      // search text could have changed before the promises resolved, so only update the state if results are still relevant
      this.setState({
        searchResults: sortedListItems.length === 0 ? [{ value: Messages.noMatchingResults(), disabled: true }] : sortedListItems,
      });
    }
  }

  private debouncedSearch = _.debounce(this.undebouncedSearch, GranteeSearchField.DEBOUNCE_MILLISECONDS);

  private handleFilterChange = (searchText: string): void => {
    if (searchText) {
      this.setState({ searchText });
      this.debouncedSearch(searchText);
    } else {
      this.setState(GranteeSearchFieldDefaultState);
    }
  }

  private handleSelect = (value: string): void => {
    const selectedGranteeListItem = this.state.searchResults.find(item => item.value === value);
    this.props.onGranteeSelect(selectedGranteeListItem.groupInfo, selectedGranteeListItem.user);
  }

  private handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    // The purpose of this callback is to clear the widget input box when changing focus to something else on the screen.
    // But there are exceptions - when interacting with anything inside the dropdown or with the dropdown itself (which can
    // happen during scrolling - TFS 1065910), the state should not be cleared.
    const elementReceivingFocus = (event.relatedTarget || document.activeElement) as HTMLElement;
    if (this.isMouseOverDropdown || this.isInsideDropdown(elementReceivingFocus)) {
      return;
    }
    this.setState(GranteeSearchFieldDefaultState);
  }

  private handleMouseDown = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    const mousedOverElement = event.target as HTMLElement;
    if (this.isInsideDropdown(mousedOverElement)) {
      this.isMouseOverDropdown = true;
    }
  }

  private handleMouseUp = (event: React.MouseEvent<HTMLElement, MouseEvent>): void => {
    const mousedOverElement = event.target as HTMLElement;
    if (this.isInsideDropdown(mousedOverElement)) {
      this.isMouseOverDropdown = false;
      // when done interacting with the dropdown, focus should go back to the widget input
      const inputElement = (ReactDOM.findDOMNode(this) as HTMLElement).getElementsByTagName('input').item(0);
      inputElement && inputElement.focus();
    }
  }

  private isInsideDropdown = (elementToCheck: HTMLElement): boolean => {
    const dropdownElement = (ReactDOM.findDOMNode(this) as HTMLElement).getElementsByClassName(this.dropdownClassName).item(0);
    // check that both elements exist
    if (dropdownElement && elementToCheck) {
      return dropdownElement.contains(elementToCheck);
    }
    return false;
  }

  public render() {
    const className = `${PermissionsGridStyles.EditableGranteeCellSearchField} ${this.state.searchResults.length > 0 && this.dropdownClassName}`;
    /* eslint-disable jsx-a11y/no-autofocus */
    return (
      <div className={PermissionsGridStyles.EditableGranteeCellSearchFieldContainer}>
        <FilteringList
          autoFocus={true}
          className={className}
          inputTestId={GranteeSearchField.INPUT_ID}
          items={this.state.searchResults}
          listTestId={GranteeSearchField.LIST_ID}
          onBlur={this.handleBlur}
          onFilterChange={this.handleFilterChange}
          onMouseDown={this.handleMouseDown}
          onMouseUp={this.handleMouseUp}
          onSelect={this.handleSelect}
          searchPlaceholder={Messages.searchForGroupOrUser()}
          searchText={this.state.searchText}
        />
      </div>
    );
  }
}
